/**
 * SolicitudCotizacion — Formulario nativo de solicitud de servicios de viaje.
 *
 * Replica el formulario HTML de producción de PD Travel y envía el mismo
 * payload al endpoint POST /api/forms/external/submit/servicios.
 *
 * Configuración por acceso (Fase 3), tomada de `authed`:
 *   - allowed_services[]:        limita los servicios visibles
 *   - allowed_flight_classes[]:  limita las clases de vuelo disponibles
 *   - allowed_hotel_chains[]:    limita las cadenas hoteleras seleccionables
 *   - user_is_default_passenger: si true, ofrece checkbox "Yo soy uno de los
 *     pasajeros" que prefill del primer pasajero con los datos del solicitante
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plane, Building2, Car, Shield, MapPin, Users as UsersIcon,
  Trash2, Plus, Loader2, Send, CheckCircle2, ClipboardList,
  ChevronDown, ChevronRight,
} from "lucide-react";

// ═══════════════════════════════ Catálogos ═══════════════════════════════

const SERVICE_OPTIONS = [
  { key: "flight",    label: "Vuelos",             icon: Plane },
  { key: "hotel",     label: "Hotel",              icon: Building2 },
  { key: "transfer",  label: "Traslados",          icon: MapPin },
  { key: "car",       label: "Renta de auto",      icon: Car },
  { key: "insurance", label: "Seguro de viajero",  icon: Shield },
  { key: "activity",  label: "Actividades",        icon: ClipboardList },
];

const FLIGHT_TYPES = ["Sencillo", "Redondo", "Multi-Destino"];
const FLIGHT_CLASSES = ["Economy Class", "Business Class", "First Class"];
const TRAVEL_TYPES = ["Nacional", "Internacional"];

const ROOM_TYPES = ["Sencilla", "Doble", "Triple"];

// Cadenas + marcas (misma lista que producción)
const HOTEL_CHAINS = {
  "Sin Preferencia": [],
  "Marriott International": [
    "Sin Preferencia", "Marriott Hotels", "JW Marriott", "Sheraton", "Westin",
    "Ritz-Carlton", "St. Regis", "Le Méridien", "Courtyard by Marriott",
    "Renaissance Hotels", "AC Hotels by Marriott", "Aloft Hotels",
  ],
  "Hilton Worldwide": [
    "Sin Preferencia", "Hilton Hotels & Resorts", "DoubleTree by Hilton",
    "Hampton by Hilton", "Embassy Suites", "Conrad Hotels", "Waldorf Astoria",
    "Hilton Garden Inn",
  ],
  "IHG (InterContinental Hotels Group)": [
    "Sin Preferencia", "InterContinental", "Crowne Plaza", "Holiday Inn",
    "Holiday Inn Express", "Kimpton", "Regent",
  ],
  "Accor Hotels": [
    "Sin Preferencia", "Sofitel", "Novotel", "Mercure", "Ibis", "Fairmont",
    "Raffles", "Pullman",
  ],
  "Wyndham Hotels & Resorts": [
    "Sin Preferencia", "Wyndham", "Ramada", "Days Inn", "Super 8", "Howard Johnson",
  ],
  "Hyatt Hotels Corporation": [
    "Sin Preferencia", "Hyatt Regency", "Grand Hyatt", "Park Hyatt", "Andaz", "Hyatt Place",
  ],
  "Choice Hotels International": [
    "Sin Preferencia", "Comfort Inn", "Comfort Suites", "Quality Inn", "Sleep Inn",
    "Clarion", "MainStay Suites",
  ],
  "Best Western Hotels & Resorts": [
    "Sin Preferencia", "Best Western", "Best Western Plus", "Best Western Premier",
  ],
  "Otras marcas de lujo o boutique": ["Sin Preferencia"],
  "Otro": [], // El usuario captura el nombre libre
};
const HOTEL_CHAIN_NAMES = Object.keys(HOTEL_CHAINS);

const TRANSFER_TYPES = [
  "Aeropuerto → Hotel",
  "Hotel → Aeropuerto",
  "Aeropuerto → Hotel → Aeropuerto",
  "Punto a punto (ej. oficina ↔ evento)",
  "Otro",
];

const CAR_TYPES = [
  "Económico - Nissan March o similar",
  "Compacto - Chevrolet Aveo o similar",
  "Lujo Elite - Chevrolet Onix o similar",
  "Extra Grande - Chevrolet Cavalier o similar",
  "Premium - Toyota Camry o similar",
  "SUV - Mitsubishi Expander o similar",
  "Premium - Chevrolet Suburban o similar",
  "Otro",
];

const INSURANCE_DESTINATIONS = [
  "Territorio Nacional",
  "Norteamérica",
  "Centroamérica y Caribe (Excepto Cuba)",
  "Sudamérica",
  "Argentina",
  "Europa",
  "Oceanía, Asia, África",
  "Países Limítrofes",
  "Múltiples destinos",
];

// ═══════════════════════════════ Aeropuertos IATA (cache global) ═══════════════════════════════

const AIRPORTS_CSV_URL = "https://cdn.jsdelivr.net/gh/fergomcri-pdtravel/AeropuertosIATA@main/aeropuertos.csv";
let _airportsCache = null;
let _airportsPromise = null;

// Homologación EN → ES para ciudades comunes (para que MEX y NLU aparezcan como "Ciudad de México", etc.)
const CITY_ALIASES = {
  "mexico city": "Ciudad de México",
  "new york": "Nueva York",
  "new york city": "Nueva York",
  "london": "Londres",
  "rome": "Roma",
  "paris": "París",
  "munich": "Múnich",
  "moscow": "Moscú",
  "havana": "La Habana",
  "milan": "Milán",
  "milano": "Milán",
  "vienna": "Viena",
  "warsaw": "Varsovia",
  "prague": "Praga",
  "athens": "Atenas",
  "lisbon": "Lisboa",
  "brussels": "Bruselas",
  "copenhagen": "Copenhague",
  "stockholm": "Estocolmo",
  "zurich": "Zúrich",
  "venice": "Venecia",
  "florence": "Florencia",
  "naples": "Nápoles",
  "turin": "Turín",
  "seville": "Sevilla",
  "cairo": "El Cairo",
  "damascus": "Damasco",
  "jerusalem": "Jerusalén",
  "tokyo": "Tokio",
  "kyoto": "Kioto",
  "beijing": "Pekín",
  "peking": "Pekín",
  "singapore": "Singapur",
  "new delhi": "Nueva Delhi",
  "rio de janeiro": "Río de Janeiro",
  "sao paulo": "São Paulo",
  "quebec city": "Ciudad de Quebec",
  "quebec": "Ciudad de Quebec",
};

// Score de concurrencia (mayor = más importante). Se usa para desempatar
// aeropuertos que comparten ciudad, priorizando el International > Regional > Water/Heliport.
const concurrencyRank = (name) => {
  const n = (name || "").toLowerCase();
  if (n.includes("international")) return 100;
  if (/\b(intercontinental|hub)\b/.test(n)) return 90;
  if (n.includes("regional")) return 40;
  if (n.includes("municipal") || n.includes("county")) return 30;
  if (n.includes("executive") || n.includes("airpark") || n.includes("business")) return 20;
  if (/\b(water|seaplane|sea|harbou?r|aerodrome|heliport|helipad|airfield|airstrip)\b/.test(n)) return 10;
  return 60; // aeropuerto genérico (típicamente principal si no hay otro con "International")
};

const parseAirportsCsv = (text) => {
  const lines = text.split(/\r?\n/).filter(Boolean);
  const [header, ...rows] = lines;
  const cols = header.split(",").map((c) => c.trim().toLowerCase());
  const iIata = cols.indexOf("iata_code");
  const iName = cols.indexOf("name");
  const iCity = cols.indexOf("city");
  const iCountryName = cols.indexOf("country_name");
  const iActive = cols.indexOf("is_active");
  const out = [];
  for (const line of rows) {
    const parts = line.split(",");
    if (parts.length < 5) continue;
    const iata = (parts[iIata] || "").trim();
    if (!iata || iata.length !== 3) continue;
    if (iActive >= 0 && (parts[iActive] || "").trim().toLowerCase() === "false") continue;
    const rawCity = (parts[iCity] || "").trim();
    const cityKey = rawCity.toLowerCase();
    const city = CITY_ALIASES[cityKey] || rawCity;
    const name = (parts[iName] || "").trim();
    out.push({
      iata: iata.toUpperCase(),
      name,
      city,
      rawCity, // versión original (útil para búsquedas en inglés como "Mexico City")
      country: (parts[iCountryName] || "").trim(),
      rank: concurrencyRank(name),
    });
  }
  return out;
};

// Normaliza texto para búsqueda: minúsculas + sin acentos.
const normalize = (s) => (s || "")
  .toString()
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "");

const loadAirports = () => {
  if (_airportsCache) return Promise.resolve(_airportsCache);
  if (_airportsPromise) return _airportsPromise;
  _airportsPromise = fetch(AIRPORTS_CSV_URL)
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error("HTTP " + r.status))))
    .then((txt) => { _airportsCache = parseAirportsCsv(txt); return _airportsCache; })
    .catch((e) => { _airportsPromise = null; throw e; });
  return _airportsPromise;
};

const useAirports = () => {
  const [data, setData] = useState(_airportsCache || []);
  useEffect(() => {
    if (!_airportsCache) loadAirports().then(setData).catch(() => setData([]));
  }, []);
  return data;
};

const AirportInput = ({ value, onChange, placeholder, testid }) => {
  const airports = useAirports();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const ref = useRef(null);
  useEffect(() => { setQuery(value || ""); }, [value]);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const matches = useMemo(() => {
    const q = normalize((query || "").trim());
    if (!q || airports.length === 0) return [];
    const isCode = q.length === 3 && /^[a-z]{3}$/.test(q);
    const results = [];
    for (const a of airports) {
      const iata = a.iata.toLowerCase();
      const city = normalize(a.city);
      const rawCity = normalize(a.rawCity);
      const name = normalize(a.name);
      const country = normalize(a.country);
      let score = 0;
      if (isCode && iata === q) score = 100;
      else if (iata.startsWith(q)) score = 80;
      else if (city.startsWith(q) || rawCity.startsWith(q)) score = 60;
      else if (city.includes(q) || rawCity.includes(q)) score = 40;
      else if (name.includes(q)) score = 20;
      else if (country.includes(q)) score = 10;
      if (score) results.push({ ...a, score });
      if (results.length >= 80) break;
    }
    // Orden: score DESC → concurrencia (rank) DESC → iata ASC.
    // Así en ciudades con múltiples aeropuertos (p.ej. Vancouver) primero sale
    // el International (YVR) y después el Water Aerodrome (CXH).
    return results
      .sort((a, b) => (b.score - a.score) || (b.rank - a.rank) || a.iata.localeCompare(b.iata))
      .slice(0, 10);
  }, [query, airports]);
  const applyMatch = (m) => {
    const label = `${m.iata} — ${m.city}, ${m.country}`;
    setQuery(label); onChange(label); setOpen(false);
  };
  return (
    <div ref={ref} className="relative">
      <Input
        value={query}
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || "MEX / Ciudad de México"}
        data-testid={testid}
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div className="absolute z-30 left-0 mt-1 w-[max(100%,560px)] max-w-[calc(100vw-2rem)] max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {matches.map((m) => (
            <button
              key={m.iata}
              type="button"
              onClick={() => applyMatch(m)}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 border-b border-slate-100 last:border-b-0 whitespace-nowrap"
            >
              <span className="font-mono text-xs font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded shrink-0">{m.iata}</span>
              <span className="text-sm text-slate-800 shrink-0">{m.city}</span>
              {m.name && (
                <>
                  <span className="text-slate-300 shrink-0">—</span>
                  <span className="italic text-[11px] text-slate-400 truncate">{m.name}</span>
                </>
              )}
              <span className="text-xs text-slate-500 ml-auto shrink-0 pl-2">{m.country}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Ciudades únicas derivadas del catálogo de aeropuertos.
// Cada entrada agrega los aeropuertos de esa ciudad y toma el rank del más
// concurrido, para que las metrópolis salgan primero al buscar.
const useCities = () => {
  const airports = useAirports();
  return useMemo(() => {
    if (!airports.length) return [];
    const map = new Map(); // key = `${city}|${country}` (aliased)
    for (const a of airports) {
      if (!a.city) continue;
      const key = `${a.city.toLowerCase()}|${(a.country || "").toLowerCase()}`;
      const prev = map.get(key);
      if (prev) {
        prev.rank = Math.max(prev.rank, a.rank);
        prev.count += 1;
      } else {
        map.set(key, {
          city: a.city,
          rawCity: a.rawCity || a.city,
          country: a.country || "",
          rank: a.rank || 0,
          count: 1,
        });
      }
    }
    return Array.from(map.values());
  }, [airports]);
};

const CityInput = ({ value, onChange, placeholder, testid }) => {
  const cities = useCities();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const ref = useRef(null);
  useEffect(() => { setQuery(value || ""); }, [value]);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const matches = useMemo(() => {
    const q = normalize((query || "").trim());
    if (!q || cities.length === 0) return [];
    const results = [];
    for (const c of cities) {
      const city = normalize(c.city);
      const rawCity = normalize(c.rawCity);
      const country = normalize(c.country);
      let score = 0;
      if (city === q || rawCity === q) score = 100;
      else if (city.startsWith(q) || rawCity.startsWith(q)) score = 80;
      else if (city.includes(q) || rawCity.includes(q)) score = 40;
      else if (country.includes(q)) score = 10;
      if (score) results.push({ ...c, score });
      if (results.length >= 120) break;
    }
    return results
      .sort((a, b) => (b.score - a.score) || (b.rank - a.rank) || a.city.localeCompare(b.city))
      .slice(0, 10);
  }, [query, cities]);
  const applyMatch = (m) => {
    const label = m.country ? `${m.city}, ${m.country}` : m.city;
    setQuery(label);
    onChange(label);
    setOpen(false);
  };
  return (
    <div ref={ref} className="relative">
      <Input
        value={query}
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || "Ciudad de México"}
        data-testid={testid}
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div className="absolute z-30 left-0 mt-1 w-[max(100%,320px)] max-w-[calc(100vw-2rem)] max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {matches.map((m) => (
            <button
              key={`${m.city}|${m.country}`}
              type="button"
              onClick={() => applyMatch(m)}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 border-b border-slate-100 last:border-b-0 whitespace-nowrap"
            >
              <span className="text-sm text-slate-800 shrink-0">{m.city}</span>
              <span className="text-xs text-slate-500 ml-auto shrink-0 pl-2">{m.country}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════ Componentes UI ═══════════════════════════════

const Section = ({ id, title, icon: Icon, children, badge, defaultOpen = true, open, onToggle, accent }) => {
  const [innerOpen, setInnerOpen] = useState(defaultOpen);
  const isControlled = typeof open === "boolean";
  const isOpen = isControlled ? open : innerOpen;
  const toggle = () => (isControlled ? onToggle?.() : setInnerOpen((v) => !v));
  const accentClass = accent || "from-blue-600 to-cyan-500";
  return (
    <section
      id={id}
      className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.05)] overflow-hidden transition-shadow hover:shadow-[0_2px_8px_rgba(15,23,42,0.08)]"
      data-testid={id ? `section-${id}` : undefined}
    >
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-3 px-5 py-4 text-left transition-colors bg-blue-50/60 hover:bg-blue-50 border-b border-blue-100"
      >
        <span className={`w-8 h-8 rounded-lg bg-gradient-to-br ${accentClass} flex items-center justify-center shrink-0 shadow-sm`}>
          {Icon && <Icon className="w-4 h-4 text-white" />}
        </span>
        <span className="flex-1 flex items-baseline gap-2 min-w-0">
          <span className="font-semibold text-slate-800 text-[15px] truncate">{title}</span>
          {badge != null && (
            <span className="text-[11px] font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </span>
        {isOpen ? <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" /> : <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />}
      </button>
      {isOpen && <div className="px-5 pt-4 pb-5 space-y-4">{children}</div>}
    </section>
  );
};

const Field = ({ label, children, required, hint, className }) => (
  <div className={className}>
    <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </Label>
    {children}
    {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
  </div>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border-2 transition-colors ${active ? "border-blue-600 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
  >
    {children}
  </button>
);

// ═══════════════════════════════ Factory helpers ═══════════════════════════════

const emptyPassenger = () => ({ fullName: "", birthDate: "", email: "", phone: "" });
const emptySegment = () => ({ origin: "", destination: "", date: "", time: "" });
const emptyFlight = () => ({
  type: "Redondo", class: "Economy Class",
  origin: "", destination: "",
  departureDate: "", departureTime: "",
  returnDate: "", returnTime: "",
  segments: [emptySegment()],
  documentedBaggage: "Si",
  preferredRoute: "",
});
const emptyHotel = () => ({
  city: "", roomType: "", preferredChain: "", preferredBrand: "",
  brandOther: "", notes: "",
});
const emptyTransfer = () => ({
  type: "",
  pickup: { date: "", time: "", place: "" },
  ret: { date: "", time: "", place: "" },
  notes: "",
});
const emptyCar = () => ({
  vehicleType: "",
  pickup: { date: "", time: "", place: "" },
  ret: { date: "", time: "", place: "" },
  notes: "",
});
const emptyInsurance = () => ({
  destination: "", residenceCountry: "México", from: "", to: "", notes: "",
});
const emptyActivity = () => ({ date: "", tour: "", notes: "" });

const buildInitialPassengers = (paxCount, prefill) => {
  const arr = Array.from({ length: paxCount }, () => emptyPassenger());
  if (prefill) arr[0] = { ...arr[0], ...prefill };
  return arr;
};

// ═══════════════════════════════ Componente principal ═══════════════════════════════

const SolicitudCotizacion = ({ authed, onLogout }) => {
  const empresa = authed?.empresa || "";
  const areas   = useMemo(() => authed?.areas || ["Dirección","Operaciones","Administración","Marketing","Otro"], [authed]);
  const reqName = authed?.nombre_completo || "";
  const reqEmail = authed?.email || "";

  // Whitelists por acceso (vacío = TODO permitido)
  const allowedServices    = useMemo(() => (authed?.allowed_services       || []).filter(Boolean), [authed]);
  const allowedFlightClasses = useMemo(() => (authed?.allowed_flight_classes || []).filter(Boolean), [authed]);
  const allowedHotelChains = useMemo(() => (authed?.allowed_hotel_chains   || []).filter(Boolean), [authed]);
  const canUserBePassenger = !!authed?.user_is_default_passenger;  // Cap global de la app: 10 pax. Para más se debe cotizar como grupo.
  const HARD_MAX_PAX = 10;
  const maxPassengers = useMemo(() => {
    const configured = Number(authed?.max_passengers || 0);
    const effective = Number.isFinite(configured) && configured > 0 ? configured : HARD_MAX_PAX;
    return Math.min(effective, HARD_MAX_PAX);
  }, [authed]);

  const visibleServices = useMemo(
    () => allowedServices.length ? SERVICE_OPTIONS.filter(s => allowedServices.includes(s.key)) : SERVICE_OPTIONS,
    [allowedServices],
  );
  const visibleFlightClasses = useMemo(
    () => allowedFlightClasses.length ? FLIGHT_CLASSES.filter(c => allowedFlightClasses.includes(c)) : FLIGHT_CLASSES,
    [allowedFlightClasses],
  );
  const visibleHotelChains = useMemo(
    // Si el admin configuró cadenas específicas, úsalas tal cual (incluye catálogo dinámico).
    // De lo contrario, mostrar el listado estático base como fallback.
    () => allowedHotelChains.length ? allowedHotelChains : HOTEL_CHAIN_NAMES,
    [allowedHotelChains],
  );

  // ═════════ Estado ═════════
  const [authArea, setAuthArea] = useState("");
  const [authAreaOther, setAuthAreaOther] = useState("");
  const [travelType, setTravelType] = useState("");
  const [services, setServices] = useState([]);
  const [paxCount, setPaxCount] = useState(1);
  const [userIsPassenger, setUserIsPassenger] = useState(!!authed?.user_is_default_passenger);
  const [passengers, setPassengers] = useState(() =>
    buildInitialPassengers(1, authed?.user_is_default_passenger ? { fullName: authed?.nombre_completo || "", email: authed?.email || "" } : null)
  );
  const [flights, setFlights] = useState([emptyFlight()]);
  const [hotels, setHotels] = useState([emptyHotel()]);
  const [transfers, setTransfers] = useState([emptyTransfer()]);
  const [carRentals, setCarRentals] = useState([emptyCar()]);
  const [insurances, setInsurances] = useState([emptyInsurance()]);
  const [activities, setActivities] = useState([emptyActivity()]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Perfil de viajero ligado: usado para pre-llenar Pasajero #1 con fechaNac / telCel.
  const [perfilData, setPerfilData] = useState(null);
  useEffect(() => {
    if (!authed?.perfil_viajero_completed) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/forms/external/perfil`, { withCredentials: true });
        if (!cancelled) setPerfilData(r.data?.datos || {});
      } catch (_) { /* silencioso */ }
    })();
    return () => { cancelled = true; };
  }, [authed?.perfil_viajero_completed]);

  // Cuando llega el perfil y el usuario está marcado como Pasajero #1,
  // completar fecha de nacimiento y teléfono si están vacíos.
  useEffect(() => {
    if (!perfilData || !userIsPassenger) return;
    setPassengers((prev) => {
      if (!prev.length) return prev;
      const next = [...prev];
      const p0 = { ...next[0] };
      let changed = false;
      if (!p0.birthDate && perfilData.fechaNac) { p0.birthDate = perfilData.fechaNac; changed = true; }
      if (!p0.phone && (perfilData.telCel || perfilData.telAlt)) { p0.phone = perfilData.telCel || perfilData.telAlt; changed = true; }
      if (changed) { next[0] = p0; return next; }
      return prev;
    });
  }, [perfilData, userIsPassenger]);

  // ═════════ Handlers básicos ═════════

  const toggleService = (key) => {
    setServices((prev) => (prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]));
  };

  const changePaxCount = (n) => {
    const raw = Number(n) || 1;
    const next = Math.max(1, Math.min(maxPassengers, raw));
    // Cuando el usuario intenta rebasar el cap global (10), avisar de la
    // ruta de solicitud grupal.
    if (raw > HARD_MAX_PAX) {
      toast.warning(
        "Para cotizaciones de 10+ pasajeros se requiere solicitud grupal. Escribe a grupos@pdtravelmex.com",
        { duration: 8000 }
      );
    }
    setPaxCount(next);
    setPassengers((prev) => {
      const arr = [...prev];
      while (arr.length < next) arr.push(emptyPassenger());
      arr.length = next;
      // Si "yo soy pasajero", asegurar prefill
      if (userIsPassenger) {
        arr[0] = { ...arr[0], fullName: reqName, email: reqEmail };
      }
      return arr;
    });
  };

  const toggleUserAsPassenger = (checked) => {
    setUserIsPassenger(checked);
    setPassengers((prev) => {
      const arr = [...prev];
      if (checked) {
        arr[0] = {
          ...arr[0],
          fullName: reqName,
          email: reqEmail,
          birthDate: arr[0].birthDate || perfilData?.fechaNac || "",
          phone: arr[0].phone || perfilData?.telCel || perfilData?.telAlt || "",
        };
      } else {
        arr[0] = { ...arr[0], fullName: "", email: "", birthDate: "", phone: "" };
      }
      return arr;
    });
  };

  const updateItem = (setter) => (i, patch) => {
    setter((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  };
  const updateNested = (setter) => (i, key, patch) => {
    setter((prev) => prev.map((it, idx) => (idx === i ? { ...it, [key]: { ...(it[key] || {}), ...patch } } : it)));
  };
  const addItem = (setter, factory, max = 5) => () => setter((prev) => (prev.length >= max ? prev : [...prev, factory()]));
  const removeItem = (setter) => (i) => setter((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));

  // ═════════ Submit ═════════

  const submit = useCallback(async () => {
    // Validaciones
    if (!authArea) return toast.error("Selecciona el área de autorización");
    if (authArea === "Otro" && !authAreaOther.trim()) return toast.error("Especifica el área");
    if (!travelType) return toast.error("Selecciona el tipo de viaje");
    if (services.length === 0) return toast.error("Elige al menos un servicio");
    for (const p of passengers) {
      if (!p.fullName || !p.birthDate || !p.email || !p.phone) {
        return toast.error("Completa todos los datos de los pasajeros (nombre, fecha nacimiento, email, teléfono)");
      }
    }

    // Construir payload igual a producción
    const flightsPayload = services.includes("flight") ? flights.map((f) => {
      if (f.type === "Multi-Destino") {
        return {
          type: "Multi-Destino",
          segments: f.segments,
          class: f.class || "",
          documentedBaggage: f.documentedBaggage || "",
          preferredRoute: f.preferredRoute || "",
          origin: null, destination: null,
          departureDate: null, departureTime: null,
          returnDate: null, returnTime: null,
        };
      }
      return {
        type: f.type,
        class: f.class || "",
        documentedBaggage: f.documentedBaggage || "",
        preferredRoute: f.preferredRoute || "",
        origin: f.origin, destination: f.destination,
        departureDate: f.departureDate, departureTime: f.departureTime,
        returnDate: f.type === "Redondo" ? f.returnDate : null,
        returnTime: f.type === "Redondo" ? f.returnTime : null,
        segments: null,
      };
    }) : undefined;

    const transfersPayload = services.includes("transfer") ? transfers.map((t) => {
      const isRound = t.type === "Aeropuerto → Hotel → Aeropuerto";
      return {
        type: t.type,
        pickup: t.pickup,
        ret: isRound ? t.ret : null,
        notes: t.notes,
      };
    }) : undefined;

    const payload = {
      timestamp: new Date().toISOString(),
      company: empresa,
      requester: {
        name: reqName,
        email: reqEmail,
        authArea,
        authAreaOther: authArea === "Otro" ? authAreaOther : null,
        companyName: empresa,
      },
      travelType,
      passengers: passengers.map((p, idx) => ({
        index: idx + 1,
        fullName: p.fullName,
        birthDate: p.birthDate,
        email: p.email,
        phone: p.phone,
        phoneCountry: p.phoneCountry || "",
        phoneDialCode: p.phoneDialCode || "",
        phoneNational: (p.phone || "").replace(/\D/g, ""),
      })),
      services,
      flights: flightsPayload,
      hotels: services.includes("hotel") ? hotels.map((h) => ({
        city: h.city,
        roomType: h.roomType,
        preferredChain: h.preferredChain,
        preferredBrand: h.preferredChain === "Otro" ? h.brandOther : (h.preferredBrand || ""),
        notes: h.notes,
      })) : undefined,
      transfers: transfersPayload,
      carRentals: services.includes("car") ? carRentals : undefined,
      travelInsurances: services.includes("insurance") ? insurances : undefined,
      activities: services.includes("activity") ? activities : undefined,
    };

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/forms/external/submit/servicios`, payload, { withCredentials: true });
      setConfirmation({ folio: res.data?.folio });
      toast.success(`Solicitud enviada · ${res.data?.folio}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar la solicitud");
    } finally {
      setSubmitting(false);
    }
  }, [authArea, authAreaOther, travelType, services, passengers, flights, hotels, transfers, carRentals, insurances, activities, empresa, reqName, reqEmail]);

  // ═════════ Confirmación ═════════
  if (confirmation) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg border border-emerald-200 p-6 md:p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">¡Solicitud enviada!</h2>
          <p className="text-sm text-slate-600 mt-2">
            Recibimos tu solicitud. Nuestro equipo Traveldesk te contactará en breve.
          </p>
          <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="text-xs text-slate-500 uppercase tracking-wide">Folio</span>
            <p className="font-mono text-lg text-slate-800 font-semibold">{confirmation.folio}</p>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
            <Button variant="outline" onClick={() => setConfirmation(null)} data-testid="btn-nueva-solicitud">Nueva solicitud</Button>
            {onLogout && (<Button variant="secondary" onClick={onLogout}>Cerrar sesión</Button>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
      {/* Solicitante */}
      <Section id="solicitante" title="Datos del solicitante" icon={UsersIcon} accent="from-blue-600 to-indigo-500">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Nombre del solicitante" required>
            <Input value={reqName} readOnly disabled className="bg-slate-50" data-testid="fld-name" />
          </Field>
          <Field label="Email" required>
            <Input value={reqEmail} readOnly disabled className="bg-slate-50" data-testid="fld-email" />
          </Field>
          <Field label="Empresa" required>
            <Input value={empresa} readOnly disabled className="bg-slate-50" data-testid="fld-empresa" />
          </Field>
          <Field label="Área de autorización" required>
            <Select value={authArea} onValueChange={setAuthArea}>
              <SelectTrigger data-testid="fld-auth-area"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent>
                {areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {authArea === "Otro" && (
            <Field label="Especifica el área" required className="md:col-span-2">
              <Input value={authAreaOther} onChange={(e) => setAuthAreaOther(e.target.value)} data-testid="fld-auth-area-other" />
            </Field>
          )}
        </div>
      </Section>

      {/* Datos del pasajero */}
      <Section id="pasajeros" title="Datos del pasajero" icon={UsersIcon} badge={paxCount} accent="from-emerald-600 to-teal-500">
        <div className="mb-3">
          <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1">
            Número de pasajeros<span className="text-red-500 ml-0.5">*</span>
          </Label>
          <div className="flex items-center gap-3 flex-wrap">
            <Input type="number" min="1" max={maxPassengers} step="1" value={paxCount} onChange={(e) => changePaxCount(e.target.value)} className="w-24" data-testid="fld-paxcount" />
            {canUserBePassenger && maxPassengers > 1 && (
              <label
                htmlFor="user-is-passenger"
                className="h-9 flex items-center gap-2 px-3 rounded-md border border-blue-200 bg-blue-50 cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  id="user-is-passenger"
                  checked={userIsPassenger}
                  onChange={(e) => toggleUserAsPassenger(e.target.checked)}
                  data-testid="fld-user-is-passenger"
                />
                <span className="font-semibold text-slate-800">Yo soy uno de los pasajeros</span>
              </label>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            {authed?.max_passengers
              ? `Máximo permitido: ${maxPassengers} pasajero${maxPassengers === 1 ? "" : "s"}.`
              : "Se generarán los campos correspondientes."}
          </p>
        </div>
        {passengers.map((p, i) => {
          const locked = userIsPassenger && i === 0;
          return (
            <div key={i} className="p-3 rounded-lg border border-slate-200 space-y-2">
              <span className="text-xs font-semibold text-slate-500 uppercase">Pasajero #{i + 1}{locked && " (tú)"}</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Field label="Nombre completo" required>
                  <Input value={p.fullName} readOnly={locked} disabled={locked} className={locked ? "bg-slate-50" : ""}
                         onChange={(e) => updateItem(setPassengers)(i, { fullName: e.target.value })} data-testid={`pax-${i}-name`} />
                </Field>
                <Field label="Fecha de nacimiento" required>
                  <Input type="date" value={p.birthDate} onChange={(e) => updateItem(setPassengers)(i, { birthDate: e.target.value })} data-testid={`pax-${i}-birth`} />
                </Field>
                <Field label="Email" required>
                  <Input type="email" value={p.email} readOnly={locked} disabled={locked} className={locked ? "bg-slate-50" : ""}
                         onChange={(e) => updateItem(setPassengers)(i, { email: e.target.value })} data-testid={`pax-${i}-email`} />
                </Field>
                <Field label="Teléfono" required hint="Solo dígitos. Ej. 5564910686 (México)">
                  <Input type="tel" value={p.phone} onChange={(e) => updateItem(setPassengers)(i, { phone: e.target.value })} placeholder="+52 55 6491 0686" data-testid={`pax-${i}-phone`} />
                </Field>
              </div>
            </div>
          );
        })}
      </Section>

      {/* Servicios */}
      <Section id="servicios" title="Servicios solicitados" icon={ClipboardList} accent="from-blue-600 to-cyan-500">
        <Field label="Tipo de viaje" required>
          <div className="flex gap-2 flex-wrap">
            {TRAVEL_TYPES.map((t) => (
              <Chip key={t} active={travelType === t} onClick={() => setTravelType(t)} testid={`chip-travel-${t.toLowerCase()}`}>{t}</Chip>
            ))}
          </div>
        </Field>
        <Field label="Selecciona el servicio que requieres" required>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {visibleServices.map(({ key, label, icon: Icon }) => {
              const active = services.includes(key);
              return (
                <button key={key} type="button" onClick={() => toggleService(key)}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${active ? "border-blue-600 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                        data-testid={`svc-toggle-${key}`}>
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${active ? "text-blue-600" : "text-slate-400"}`} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </Field>
      </Section>

      {/* VUELOS */}
      {services.includes("flight") && (
        <Section id="vuelos" title="Vuelos" icon={Plane} badge={flights.length} accent="from-sky-600 to-blue-500">
          {flights.map((f, i) => (
            <div key={i} className="p-3 rounded-lg border border-slate-200 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 uppercase">Vuelo #{i + 1}</span>
                {flights.length > 1 && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:bg-red-50" onClick={() => removeItem(setFlights)(i)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              <Field label="Tipo de vuelo" required>
                <div className="flex gap-2 flex-wrap">
                  {FLIGHT_TYPES.map((t) => (
                    <Chip key={t} active={f.type === t} onClick={() => updateItem(setFlights)(i, { type: t })}>{t}</Chip>
                  ))}
                </div>
              </Field>

              {f.type === "Multi-Destino" ? (
                <div className="space-y-2">
                  {f.segments.map((seg, si) => (
                    <div key={si} className="grid grid-cols-2 md:grid-cols-4 gap-2 p-2 rounded border border-slate-100">
                      <Field label={`Segmento ${si + 1} — Origen`} required>
                        <AirportInput value={seg.origin} onChange={(v) => {
                          const segs = [...f.segments]; segs[si] = { ...segs[si], origin: v };
                          updateItem(setFlights)(i, { segments: segs });
                        }} />
                      </Field>
                      <Field label="Destino" required>
                        <AirportInput value={seg.destination} onChange={(v) => {
                          const segs = [...f.segments]; segs[si] = { ...segs[si], destination: v };
                          updateItem(setFlights)(i, { segments: segs });
                        }} />
                      </Field>
                      <Field label="Fecha" required>
                        <Input type="date" value={seg.date} onChange={(e) => {
                          const segs = [...f.segments]; segs[si] = { ...segs[si], date: e.target.value };
                          updateItem(setFlights)(i, { segments: segs });
                        }} />
                      </Field>
                      <Field label="Hora">
                        <Input type="time" value={seg.time} onChange={(e) => {
                          const segs = [...f.segments]; segs[si] = { ...segs[si], time: e.target.value };
                          updateItem(setFlights)(i, { segments: segs });
                        }} />
                      </Field>
                      {f.segments.length > 1 && (
                        <div className="md:col-span-4">
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50 text-xs" onClick={() => {
                            const segs = f.segments.filter((_, idx) => idx !== si);
                            updateItem(setFlights)(i, { segments: segs });
                          }}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar segmento
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                  {f.segments.length < 5 && (
                    <Button size="sm" variant="outline" onClick={() => {
                      updateItem(setFlights)(i, { segments: [...f.segments, emptySegment()] });
                    }}>
                      <Plus className="w-4 h-4 mr-1" /> Agregar segmento
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Field label="Origen" required>
                    <AirportInput value={f.origin} onChange={(v) => updateItem(setFlights)(i, { origin: v })} testid={`flight-${i}-origin`} />
                  </Field>
                  <Field label="Destino" required>
                    <AirportInput value={f.destination} onChange={(v) => updateItem(setFlights)(i, { destination: v })} testid={`flight-${i}-destination`} />
                  </Field>
                  <Field label="Fecha salida" required>
                    <Input type="date" value={f.departureDate} onChange={(e) => updateItem(setFlights)(i, { departureDate: e.target.value })} />
                  </Field>
                  <Field label="Hora salida">
                    <Input type="time" value={f.departureTime} onChange={(e) => updateItem(setFlights)(i, { departureTime: e.target.value })} />
                  </Field>
                  {f.type === "Redondo" && (
                    <>
                      <Field label="Fecha regreso" required>
                        <Input type="date" value={f.returnDate} onChange={(e) => updateItem(setFlights)(i, { returnDate: e.target.value })} />
                      </Field>
                      <Field label="Hora regreso">
                        <Input type="time" value={f.returnTime} onChange={(e) => updateItem(setFlights)(i, { returnTime: e.target.value })} />
                      </Field>
                    </>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Field label="Clase" required>
                  <Select value={f.class} onValueChange={(v) => updateItem(setFlights)(i, { class: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                    <SelectContent>{visibleFlightClasses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="¿Requiere equipaje documentado?" required className="md:col-span-2">
                  <div className="flex gap-2">
                    <Chip active={f.documentedBaggage === "Si"} onClick={() => updateItem(setFlights)(i, { documentedBaggage: "Si" })}>Sí</Chip>
                    <Chip active={f.documentedBaggage === "No"} onClick={() => updateItem(setFlights)(i, { documentedBaggage: "No" })}>No</Chip>
                  </div>
                </Field>
              </div>

              <Field label="Ruta sugerida, preferente, requerimientos especiales u observaciones">
                <Textarea rows={2} value={f.preferredRoute} onChange={(e) => updateItem(setFlights)(i, { preferredRoute: e.target.value })} />
              </Field>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addItem(setFlights, emptyFlight)}>
            <Plus className="w-4 h-4 mr-1" /> Agregar vuelo
          </Button>
        </Section>
      )}

      {/* HOTELES */}
      {services.includes("hotel") && (
        <Section id="hoteles" title="Hotel" icon={Building2} badge={hotels.length} accent="from-purple-600 to-fuchsia-500">
          {hotels.map((h, i) => {
            const chainBrands = HOTEL_CHAINS[h.preferredChain] || [];
            return (
              <div key={i} className="p-3 rounded-lg border border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 uppercase">Hotel #{i + 1}</span>
                  {hotels.length > 1 && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:bg-red-50" onClick={() => removeItem(setHotels)(i)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Field label="Ciudad" required>
                    <CityInput value={h.city} onChange={(v) => updateItem(setHotels)(i, { city: v })} testid={`hotel-${i}-city`} />
                  </Field>
                  <Field label="Tipo de habitación" required>
                    <Select value={h.roomType} onValueChange={(v) => updateItem(setHotels)(i, { roomType: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                      <SelectContent>{ROOM_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Cadena hotelera de preferencia" required>
                    <Select value={h.preferredChain} onValueChange={(v) => updateItem(setHotels)(i, { preferredChain: v, preferredBrand: "", brandOther: "" })}>
                      <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                      <SelectContent>{visibleHotelChains.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  {chainBrands.length > 0 && (
                    <Field label="Hotel / marca específica">
                      <Select value={h.preferredBrand} onValueChange={(v) => updateItem(setHotels)(i, { preferredBrand: v })}>
                        <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                        <SelectContent>{chainBrands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                  )}
                  {h.preferredChain === "Otro" && (
                    <Field label="Escribe el nombre del hotel" required>
                      <Input value={h.brandOther} onChange={(e) => updateItem(setHotels)(i, { brandOther: e.target.value })} />
                    </Field>
                  )}
                </div>
                <Field label="Requerimientos especiales">
                  <Textarea rows={2} value={h.notes} onChange={(e) => updateItem(setHotels)(i, { notes: e.target.value })} />
                </Field>
              </div>
            );
          })}
          <Button variant="outline" size="sm" onClick={addItem(setHotels, emptyHotel)}>
            <Plus className="w-4 h-4 mr-1" /> Agregar hotel
          </Button>
        </Section>
      )}

      {/* TRASLADOS */}
      {services.includes("transfer") && (
        <Section id="traslados" title="Traslados" icon={MapPin} badge={transfers.length} accent="from-amber-600 to-orange-500">
          {transfers.map((t, i) => {
            const showReturn = t.type === "Aeropuerto → Hotel → Aeropuerto";
            return (
              <div key={i} className="p-3 rounded-lg border border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 uppercase">Traslado #{i + 1}</span>
                  {transfers.length > 1 && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:bg-red-50" onClick={() => removeItem(setTransfers)(i)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                <Field label="Tipo de traslado" required>
                  <Select value={t.type} onValueChange={(v) => updateItem(setTransfers)(i, { type: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                    <SelectContent>{TRANSFER_TYPES.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Field label="Recoger — Fecha" required>
                    <Input type="date" value={t.pickup.date} onChange={(e) => updateNested(setTransfers)(i, "pickup", { date: e.target.value })} />
                  </Field>
                  <Field label="Recoger — Hora" required>
                    <Input type="time" value={t.pickup.time} onChange={(e) => updateNested(setTransfers)(i, "pickup", { time: e.target.value })} />
                  </Field>
                  <Field label="Recoger — Lugar" required>
                    <Input value={t.pickup.place} onChange={(e) => updateNested(setTransfers)(i, "pickup", { place: e.target.value })} />
                  </Field>
                </div>
                {showReturn && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-2 rounded border border-slate-100 bg-slate-50">
                    <Field label="Regreso — Fecha">
                      <Input type="date" value={t.ret.date} onChange={(e) => updateNested(setTransfers)(i, "ret", { date: e.target.value })} />
                    </Field>
                    <Field label="Regreso — Hora">
                      <Input type="time" value={t.ret.time} onChange={(e) => updateNested(setTransfers)(i, "ret", { time: e.target.value })} />
                    </Field>
                    <Field label="Regreso — Lugar">
                      <Input value={t.ret.place} onChange={(e) => updateNested(setTransfers)(i, "ret", { place: e.target.value })} />
                    </Field>
                  </div>
                )}
                <Field label="Requerimientos especiales">
                  <Textarea rows={2} value={t.notes} onChange={(e) => updateItem(setTransfers)(i, { notes: e.target.value })} />
                </Field>
              </div>
            );
          })}
          <Button variant="outline" size="sm" onClick={addItem(setTransfers, emptyTransfer)}>
            <Plus className="w-4 h-4 mr-1" /> Agregar traslado
          </Button>
        </Section>
      )}

      {/* RENTA DE AUTO */}
      {services.includes("car") && (
        <Section id="renta-auto" title="Renta de auto" icon={Car} badge={carRentals.length} accent="from-red-600 to-rose-500">
          {carRentals.map((c, i) => (
            <div key={i} className="p-3 rounded-lg border border-slate-200 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 uppercase">Renta #{i + 1}</span>
                {carRentals.length > 1 && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:bg-red-50" onClick={() => removeItem(setCarRentals)(i)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              <Field label="Tipo de vehículo" required>
                <Select value={c.vehicleType} onValueChange={(v) => updateItem(setCarRentals)(i, { vehicleType: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                  <SelectContent>{CAR_TYPES.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Field label="Recoger — Lugar" required>
                  <Input value={c.pickup.place} onChange={(e) => updateNested(setCarRentals)(i, "pickup", { place: e.target.value })} />
                </Field>
                <Field label="Recoger — Fecha" required>
                  <Input type="date" value={c.pickup.date} onChange={(e) => updateNested(setCarRentals)(i, "pickup", { date: e.target.value })} />
                </Field>
                <Field label="Recoger — Hora" required>
                  <Input type="time" value={c.pickup.time} onChange={(e) => updateNested(setCarRentals)(i, "pickup", { time: e.target.value })} />
                </Field>
                <Field label="Devolver — Lugar" required>
                  <Input value={c.ret.place} onChange={(e) => updateNested(setCarRentals)(i, "ret", { place: e.target.value })} />
                </Field>
                <Field label="Devolver — Fecha" required>
                  <Input type="date" value={c.ret.date} onChange={(e) => updateNested(setCarRentals)(i, "ret", { date: e.target.value })} />
                </Field>
                <Field label="Devolver — Hora" required>
                  <Input type="time" value={c.ret.time} onChange={(e) => updateNested(setCarRentals)(i, "ret", { time: e.target.value })} />
                </Field>
              </div>
              <Field label="Requerimientos especiales">
                <Textarea rows={2} value={c.notes} onChange={(e) => updateItem(setCarRentals)(i, { notes: e.target.value })} />
              </Field>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addItem(setCarRentals, emptyCar)}>
            <Plus className="w-4 h-4 mr-1" /> Agregar renta de auto
          </Button>
        </Section>
      )}

      {/* SEGUROS */}
      {services.includes("insurance") && (
        <Section id="seguros" title="Seguro de viajero" icon={Shield} badge={insurances.length} accent="from-teal-600 to-emerald-500">
          {insurances.map((s, i) => (
            <div key={i} className="p-3 rounded-lg border border-slate-200 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 uppercase">Seguro #{i + 1}</span>
                {insurances.length > 1 && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:bg-red-50" onClick={() => removeItem(setInsurances)(i)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Field label="Destino" required>
                  <Select value={s.destination} onValueChange={(v) => updateItem(setInsurances)(i, { destination: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                    <SelectContent>{INSURANCE_DESTINATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="País de residencia" required>
                  <Input value={s.residenceCountry} onChange={(e) => updateItem(setInsurances)(i, { residenceCountry: e.target.value })} />
                </Field>
                <Field label="Desde" required>
                  <Input type="date" value={s.from} onChange={(e) => updateItem(setInsurances)(i, { from: e.target.value })} />
                </Field>
                <Field label="Hasta" required>
                  <Input type="date" value={s.to} onChange={(e) => updateItem(setInsurances)(i, { to: e.target.value })} />
                </Field>
              </div>
              <Field label="Requerimientos especiales">
                <Textarea rows={2} value={s.notes} onChange={(e) => updateItem(setInsurances)(i, { notes: e.target.value })} />
              </Field>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addItem(setInsurances, emptyInsurance)}>
            <Plus className="w-4 h-4 mr-1" /> Agregar seguro
          </Button>
        </Section>
      )}

      {/* ACTIVIDADES */}
      {services.includes("activity") && (
        <Section id="actividades" title="Actividades" icon={ClipboardList} badge={activities.length} accent="from-pink-600 to-rose-500">
          {activities.map((a, i) => (
            <div key={i} className="p-3 rounded-lg border border-slate-200 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 uppercase">Actividad #{i + 1}</span>
                {activities.length > 1 && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:bg-red-50" onClick={() => removeItem(setActivities)(i)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Field label="Fecha" required>
                  <Input type="date" value={a.date} onChange={(e) => updateItem(setActivities)(i, { date: e.target.value })} />
                </Field>
                <Field label="Tipo de tour" required>
                  <Input value={a.tour} onChange={(e) => updateItem(setActivities)(i, { tour: e.target.value })} placeholder="Ej. Tour ciudad, Traslado privado, Museo…" />
                </Field>
              </div>
              <Field label="Notas adicionales">
                <Textarea rows={2} value={a.notes} onChange={(e) => updateItem(setActivities)(i, { notes: e.target.value })} />
              </Field>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addItem(setActivities, emptyActivity)}>
            <Plus className="w-4 h-4 mr-1" /> Agregar actividad
          </Button>
        </Section>
      )}

      </div>

      {/* Footer branding */}
      <div className="pb-24 pt-6 px-4 flex flex-col items-center gap-2 text-slate-500">
        <span className="text-[11px] uppercase tracking-[0.15em] font-medium">Powered by PD Travel</span>
        <img
          src="https://customer-assets-lqy194kg.emergentagent.net/job_e418166c-e368-475e-9fd1-c54940c21065/artifacts/t8kilc9s_Logo%20PD%20Cliente.png"
          alt="PD Travel"
          className="h-10 w-auto opacity-90"
          data-testid="form-footer-logo"
        />
      </div>

      {/* Envío */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-2px_8px_rgba(15,23,42,0.06)] z-20">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-3 flex items-center justify-end gap-2">
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md"
            size="lg"
            data-testid="btn-submit-cotizacion"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Enviar solicitud
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SolicitudCotizacion;
